import type { ActivityEntry } from '@bekebi/contracts/api';
import { ROLES, type Role } from '@bekebi/domain';
import { roleLabel } from '../../session/roles';

/**
 * Words for the API's stable audit action codes (packages/db ACTIVITY_ACTIONS). An unknown code is
 * shown as the code itself, never guessed.
 */
export const ACTION_LABELS: Record<string, string> = {
  'auth.login_succeeded': 'Signed in',
  'auth.login_failed': 'Sign-in failed',
  'auth.logout': 'Signed out',
  'user.created': 'User added',
  'user.updated': 'User changed',
  'user.password_reset': 'Password reset',
  'account_link.issued': 'Sign-in link issued',
  'account_link.email_sent': 'Sign-in link email accepted by the mail server',
  'account_link.email_failed': 'Sign-in link email not sent',
  'account_link.rejected': 'Used or expired sign-in link refused',
  'user.password_set': 'Password chosen through a sign-in link',
  'user.password_changed': 'Password changed',
  'service.created': 'Service added',
  'service.updated': 'Service changed',
  'resource.created': 'Resource added',
  'resource.updated': 'Resource changed',
  'binding.created': 'Service bound to a resource',
  'binding.removed': 'Binding removed',
  'renewal.opened': 'Renewal term opened',
  'renewal.updated': 'Renewal term changed',
  'renewal.renewed': 'Renewal term renewed',
  'payment_account.set': 'Payment Account set',
  'metric.entered': 'Storage plan size entered',
  'mailbox.created': 'Mailbox created',
  'mailbox.quota_changed': 'Mailbox quota changed',
  'mailbox.create_failed': 'Mailbox creation failed',
  'mailbox.quota_change_failed': 'Mailbox quota change failed',
  'monitoring_check.created': 'Monitoring check added',
  'monitoring_check.updated': 'Monitoring check changed',
  'alert_email.test_sent': 'Test email accepted by the mail server',
  'alert_email.test_failed': 'Test email not sent',
};

export const actionLabel = (code: string): string => ACTION_LABELS[code] ?? code;

/** Who acted, by the kind the API records (a user's role at the time, or not a person). */
export function actorKindLabel(actorType: ActivityEntry['actorType']): string {
  if (actorType === 'system') return 'System';
  if (actorType === 'integration') return 'Integration';
  return roleLabel(actorType);
}

const isRole = (value: unknown): value is Role =>
  typeof value === 'string' && (ROLES as readonly string[]).includes(value);

/** "bankAccountNumber" → "Bank account number". */
export function fieldLabel(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A recorded value as text. Role values of users read Admin / ICT; nothing is invented. */
export function valueText(value: unknown, key: string, entityType: string | null): string {
  if (value === null || value === undefined) return 'none';
  if (key === 'role' && entityType === 'user' && isRole(value)) return roleLabel(value);
  if (key === 'status' && entityType === 'user' && (value === 'active' || value === 'disabled')) {
    return value === 'active' ? 'Active' : 'Disabled';
  }
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

export interface ChangeLine {
  field: string;
  text: string;
}

/**
 * The entry's recorded changes as readable lines: "before → after" for recorded diffs, otherwise
 * each recorded value. Exactly what the API returned (secret-looking values are already redacted
 * by the API before they are stored).
 */
export function changeLines(entry: ActivityEntry): ChangeLine[] {
  const changes = entry.changes;
  if (!changes) return [];
  const { before, after, ...rest } = changes as {
    before?: unknown;
    after?: unknown;
    [key: string]: unknown;
  };
  const lines: ChangeLine[] = [];
  const isObject = (v: unknown): v is Record<string, unknown> =>
    v !== null && typeof v === 'object' && !Array.isArray(v);
  if (isObject(before) && isObject(after)) {
    for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
      lines.push({
        field: fieldLabel(key),
        text: `${valueText(before[key], key, entry.entityType)} → ${valueText(after[key], key, entry.entityType)}`,
      });
    }
  } else {
    if (before !== undefined) lines.push({ field: 'Before', text: valueText(before, '', null) });
    if (after !== undefined) lines.push({ field: 'After', text: valueText(after, '', null) });
  }
  for (const [key, value] of Object.entries(rest)) {
    lines.push({ field: fieldLabel(key), text: valueText(value, key, entry.entityType) });
  }
  return lines;
}
